import type {
  SdkLiveSessionAnswer,
  SdkLiveSessionRequest,
} from "@magnus/contract/voice";
import {
  commentaryCommand,
  LIVE_DATA_CHANNEL,
  LIVE_SWAP_HISTORY_MAX_MESSAGES,
  LIVE_SWAP_HISTORY_MAX_TOKENS,
  type LiveCommand,
  type LiveVoice,
  parseLiveEvent,
  trimLiveHistory,
} from "@workspace/voice-shared/live";
import type { SdkRestResult } from "../../rest";
import type { Call } from "./call-session";
import { closeLeg, setRemoteAudio } from "./leg";
import { sessionErrorMessage } from "./messages";
import type {
  LiveCallDataChannel,
  LiveCallLog,
  LiveCallMessages,
  LiveCallPeerConnection,
  LiveCallPlatform,
  LiveCallStream,
} from "./platform";

export type SwitchVoiceDeps = {
  platform: LiveCallPlatform;
  messages: LiveCallMessages;
  log: LiveCallLog;
  sessionStartTimeoutMs: number;
  onCancel: () => void;
  set: (patch: {
    phase: "switching" | "listening";
    delegatingQuery?: null;
  }) => void;
  notify: (message: string) => void;
  send: (call: Call, command: LiveCommand) => void;
  /** Re-sends what a fresh leg must hear first: mute state and notes. */
  replayLeg: (call: Call) => void;
  mintSession: (
    call: Call,
    pc: LiveCallPeerConnection,
    body: Omit<SdkLiveSessionRequest, "sdp" | "timeZone" | "callSessionId">
  ) => Promise<SdkRestResult<SdkLiveSessionAnswer>>;
  attachLeg: (
    call: Call,
    pc: LiveCallPeerConnection,
    dc: LiveCallDataChannel
  ) => void;
  wireTools: (call: Call) => void;
};

/**
 * Replaces the provider session mid-call: mints a second leg on the same
 * mic track with the transcript as history, waits for it to start, then
 * cuts over. A failure keeps the current leg and voice.
 */
export async function switchCallVoice(
  call: Call,
  voice: LiveVoice,
  deps: SwitchVoiceDeps
): Promise<boolean> {
  const stream = call.stream;
  const track = stream?.getAudioTracks()[0];
  if (!(stream && track) || !call.ready || call.swapping || call.ended) {
    return false;
  }
  if (voice === call.voice) {
    return true;
  }
  call.swapping = true;
  deps.set({ phase: "switching" });

  const interruptedQuery = call.runner?.isBusy() ? call.activeQuery : null;
  if (interruptedQuery) {
    deps.onCancel();
    await call.runner?.interrupt();
  }

  const pc = deps.platform.createPeerConnection();
  const dc = pc.createDataChannel(LIVE_DATA_CHANNEL);
  const inbound: { stream: LiveCallStream | null } = { stream: null };
  pc.ontrack = (ev) => {
    if (ev.track) {
      ev.track.enabled = false;
    }
    inbound.stream = ev.streams[0] ?? null;
  };
  let startTimer: ReturnType<typeof setTimeout> | null = null;
  let startedResolve: () => void = () => undefined;
  let startedReject: (err: Error) => void = () => undefined;
  const started = new Promise<void>((resolve, reject) => {
    startedResolve = resolve;
    startedReject = reject;
  });
  started.catch(() => undefined);
  dc.onmessage = (ev) => {
    if (parseLiveEvent(ev.data)?.type === "session.started") {
      startedResolve();
    }
  };
  dc.onclose = () => startedReject(new Error("closed"));
  const abandon = () => {
    if (startTimer) {
      clearTimeout(startTimer);
    }
    closeLeg(pc, dc);
  };
  let failure = "Couldn’t switch the voice. Still on the current one.";

  try {
    const sender = pc.addTrack(track, stream);
    const res = await deps.mintSession(call, pc, {
      voice,
      ...(call.chatId ? { chatId: call.chatId } : {}),
      history: trimLiveHistory(call.transcript.turns(), {
        maxMessages: LIVE_SWAP_HISTORY_MAX_MESSAGES,
        maxTokens: LIVE_SWAP_HISTORY_MAX_TOKENS,
      }),
      ...(interruptedQuery ? { interruptedQuery } : {}),
    });
    if (call.ended) {
      abandon();
      return false;
    }
    if (!res.ok) {
      failure = sessionErrorMessage(res, deps.messages);
      throw new Error(failure);
    }
    await pc.setRemoteDescription({ type: "answer", sdp: res.value.sdp });
    startTimer = setTimeout(
      () => startedReject(new Error("timeout")),
      deps.sessionStartTimeoutMs
    );
    await started;
    clearTimeout(startTimer);
    if (call.ended) {
      abandon();
      return false;
    }

    if (call.pc) {
      closeLeg(call.pc, call.dc);
    }
    call.runner?.dispose();
    call.loop?.dispose();
    call.pc = pc;
    call.dc = dc;
    call.sender = sender;
    call.voice = voice;
    call.backendBusy = false;
    call.cancelled = false;
    call.activeQuery = null;
    call.legStartMs = Date.now() - call.sessionEpoch;
    call.transcript.break();
    deps.attachLeg(call, pc, dc);
    deps.wireTools(call);
    if (inbound.stream) {
      call.media?.playRemote(inbound.stream);
    }
    setRemoteAudio(pc, true);
    deps.replayLeg(call);
    deps.send(call, commentaryCommand(res.value.greeting));
    call.lastActivity = Date.now();
    call.swapping = false;
    deps.set({ delegatingQuery: null, phase: "listening" });
    return true;
  } catch (err) {
    abandon();
    if (!call.ended) {
      deps.log("error", "voice switch failed", err);
      call.swapping = false;
      deps.set({ phase: "listening" });
      deps.notify(failure);
    }
    return false;
  }
}
